import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, CloudArrowUp, DownloadSimple, FolderDashed, FolderOpen, FolderPlus, Globe, LockSimple, MagnifyingGlass, PencilSimple, UploadSimple, WarningCircle, X } from '@phosphor-icons/react';
import {
  AnsichtUmschalter, BestaetigenDialog, FolderTree, ImmoButton, LeerZustand, ObjektPicker, SchwebenderToast, useToastPill,
  type ObjektPickerOption, type OrdnerKnoten,
} from '../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import EmailVerfassen from '../../emails/EmailVerfassen';
import { supabase } from '../../../../lib/supabase/client';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import {
  ORDNER_MIME, benenneEintragUm, istGoogleFormat, ladeDateiBlob, ladeDateiHoch, legeOrdnerstrukturAn, starteTextauszug, stelleWiederHer,
  verschiebeEintrag, verschiebeInPapierkorb, type BaumKnoten, type DriveEintrag,
} from '../../../../lib/dokumente/aktenschrankApi';
import { elternPfade, mitEndung, trenneEndung } from '../../../../lib/dokumente/aktenschrank';
import { useAktenschrank, type Ansicht } from '../../../../lib/dokumente/useAktenschrank';
import { meldeDokumenteGeaendert } from '../../../../lib/dokumente/useDokumenteKopf';
import { legeEntwurfAusPdfAn } from '../../../../lib/dokumente/signaturApi';
import { istInterneAblage } from '../../../../lib/dokumente/kennzahlen';
import { driveDateiUrl } from '../../../../lib/dokumente/ablageApi';
import { InhaltKopf, OrdnerInhalt, type DateiAktionen, type Markierung, type UnterordnerInfo } from './OrdnerInhalt';
import { AuswahlKaestchen } from '../../../../components/ui/AuswahlKaestchen';
import { Segment } from '../../../../components/ui/Segment';
import { FreigabeDialog } from '../../../../components/dokumente/FreigabeDialog';
import { PapierkorbKnopf } from '../../../../components/dokumente/PapierkorbKnopf';
import {
  FREIGABEN_GEAENDERT, entziehe, ladeFreigabenFuerObjekt, meldeFreigabenGeaendert, nachDokument, type Freigabe, type FreigabeDok,
} from '../../../../lib/dokumente/freigaben';
import { VorschauPanel } from './VorschauPanel';
import { PostVersandDialog } from '../post/PostVersandDialog';
import { VerschiebenDialog } from './VerschiebenDialog';
import { SuchErgebnisse } from './SuchErgebnisse';
import { ordnerFarbe } from './dateiDarstellung';
import { VerwaltungHinweis } from './VerwaltungHinweis';
import { MAIL_FORM_META } from '../../../../components/island/form/mailMeta';
import { AnKollegenSendenDialog } from '../../../../components/chat/AnKollegenSendenDialog';

const SPEICHER_OBJEKT = 'immo-dokumente-objekt';
const SPEICHER_ANSICHT = 'immo-dokumente-ansicht';
const lies = (k: string): string | null => { try { return window.localStorage.getItem(k); } catch { return null; } };
const merke = (k: string, v: string) => { try { window.localStorage.setItem(k, v); } catch { /* privat/gesperrt */ } };
const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);

function useFensterBreite(): number {
  const [b, setB] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    const neu = () => setB(window.innerWidth);
    window.addEventListener('resize', neu);
    return () => window.removeEventListener('resize', neu);
  }, []);
  return b;
}

interface UploadStand { name: string; aktuell: number; gesamt: number }

/**
 * Aktenschrank (Handoff Dokumente §6.2): Objekt-Dropdown, globale Suche, Ordnerbaum, Live-Inhalt aus Drive,
 * Datei-Aktionen mit Rückgängig, Vorschau, Upload per Drag & Drop. Mobil (§7): Baum oben (< 700 px) bzw. 210 px daneben.
 */
export function AktenschrankTab({ hochladenRef, userId }: { hochladenRef: React.MutableRefObject<(() => void) | null>; userId: string | null }) {
  const a = useAktenschrank();
  const navigate = useNavigate();
  const { openForm, closeForm } = useIslandActions();
  const { mobil, schmal } = useMobilModus();
  const breite = useFensterBreite();
  const [toast, zeigeToast] = useToastPill(3200);
  const [anKollegen, setAnKollegen] = useState<DriveEintrag | null>(null);
  const [perPost, setPerPost] = useState<DriveEintrag | null>(null);

  // ── Objekt vorwählen: zuletzt gewähltes, sonst das erste mit Drive-Ordner ─────────
  useEffect(() => {
    if (!a.objekte || a.objektId || a.sucheAktiv) return;
    const gemerkt = lies(SPEICHER_OBJEKT);
    const ziel = a.objekte.find(o => o.id === gemerkt) ?? a.objekte.find(o => o.driveId) ?? a.objekte[0];
    if (ziel) a.waehleObjekt(ziel.id, true);
  }, [a.objekte, a.objektId, a.sucheAktiv, a.waehleObjekt]);
  useEffect(() => { if (a.objektId) merke(SPEICHER_OBJEKT, a.objektId); }, [a.objektId]);

  const [ansicht, setAnsicht] = useState<Ansicht>(() => (lies(SPEICHER_ANSICHT) === 'kacheln' ? 'kacheln' : 'liste'));
  const wechsleAnsicht = (v: Ansicht) => { setAnsicht(v); merke(SPEICHER_ANSICHT, v); };

  const [suchText, setSuchText] = useState(a.q);
  useEffect(() => { setSuchText(a.q); }, [a.q]);

  const pickerOptionen: ObjektPickerOption[] = useMemo(() => (a.objekte ?? []).map(o => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '),
    suchtext: `${o.nummer} ${o.ort}`, hinweis: o.driveId ? undefined : 'kein Drive-Ordner',
  })), [a.objekte]);

  // ── Baum ────────────────────────────────────────────────────────────────
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const aktuellerKnoten = a.ordnerId ? a.index.nachId.get(a.ordnerId) ?? null : null;
  useEffect(() => { setOffen(new Set()); }, [a.objektId]);
  useEffect(() => {
    if (!aktuellerKnoten) return;
    const ids = elternPfade(aktuellerKnoten.pfad).map(p => a.index.nachPfad.get(p)?.gdriveId).filter((x): x is string => !!x);
    setOffen(alt => (ids.every(i => alt.has(i)) ? alt : new Set([...alt, ...ids])));
  }, [aktuellerKnoten, a.index]);
  const baumKnoten = useMemo(() => {
    const wandle = (k: BaumKnoten): OrdnerKnoten => ({ id: k.gdriveId, name: k.name, kinder: k.kinder.map(wandle), zaehler: a.zaehler.get(k.pfad), farbe: ordnerFarbe(k.pfad).fg });
    return a.baum.map(wandle);
  }, [a.baum, a.zaehler]);
  const umschalten = useCallback((id: string) => setOffen(alt => { const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);

  // ── Datei-Aktionen ──────────────────────────────────────────────────────
  const [laufende, setLaufende] = useState<ReadonlySet<string>>(new Set());
  const laeuft = (id: string, an: boolean) => setLaufende(alt => { const n = new Set(alt); if (an) n.add(id); else n.delete(id); return n; });
  const [umbenennen, setUmbenennen] = useState<{ id: string; wert: string } | null>(null);
  const [papierkorbId, setPapierkorbId] = useState<string | null>(null);
  const papierkorbTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [verschieben, setVerschieben] = useState<DriveEintrag | null>(null);
  useEffect(() => () => { if (papierkorbTimer.current) clearTimeout(papierkorbTimer.current); }, []);
  useEffect(() => { setUmbenennen(null); setPapierkorbId(null); }, [a.ordnerId]);

  // ── #607 F3: Mehrfachauswahl + Portal-Freigaben ─────────────────────────
  const [markiert, setMarkiert] = useState<ReadonlySet<string>>(new Set());
  const [freigabeFilter, setFreigabeFilter] = useState<'alle' | 'frei' | 'nicht'>('alle');
  const [freigaben, setFreigaben] = useState<ReadonlyMap<string, Freigabe[]>>(new Map());
  const [freigabeRunde, setFreigabeRunde] = useState(0);
  const [freigabeDialog, setFreigabeDialog] = useState<null | 'neu' | 'aendern'>(null);
  const [entziehenFrage, setEntziehenFrage] = useState(false);
  const [entziehtGerade, setEntziehtGerade] = useState(false);
  useEffect(() => { setMarkiert(new Set()); }, [a.ordnerId, a.objektId]);
  useEffect(() => {
    if (!a.objektId) { setFreigaben(new Map()); return; }
    let aktiv = true;
    ladeFreigabenFuerObjekt(a.objektId)
      .then(f => { if (aktiv) setFreigaben(nachDokument(f)); })
      .catch(() => { if (aktiv) setFreigaben(new Map()); });
    return () => { aktiv = false; };
  }, [a.objektId, freigabeRunde]);
  useEffect(() => {
    const neu = () => setFreigabeRunde(r => r + 1);
    window.addEventListener(FREIGABEN_GEAENDERT, neu);
    return () => window.removeEventListener(FREIGABEN_GEAENDERT, neu);
  }, []);
  const freigabenVon = (e: DriveEintrag): Freigabe[] | null => {
    const m = a.metaNachDatei.get(e.id);
    return m ? (freigaben.get(m.id) ?? []) : null;
  };

  const ordnerId = a.ordnerId;
  const aktualisiereHook = a.aktualisiere;
  const aktualisiere = useCallback((ids: (string | null)[]) => { aktualisiereHook(ids.filter((x): x is string => !!x)); meldeDokumenteGeaendert(); }, [aktualisiereHook]);

  const herunterladen = useCallback(async (d: DriveEintrag) => {
    try {
      const blob = await ladeDateiBlob(d.id, d.mimeType);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = d.name;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { zeigeToast(fehlerText(e, 'Download fehlgeschlagen'), true); }
  }, [zeigeToast]);

  const bestaetigeUmbenennen = async (d: DriveEintrag) => {
    if (!umbenennen) return;
    const { endung } = trenneEndung(d.name);
    const neu = mitEndung(umbenennen.wert, endung);
    if (!umbenennen.wert.trim() || neu === d.name) { setUmbenennen(null); return; }
    laeuft(d.id, true);
    try {
      const { neuerPfad } = await benenneEintragUm(d.id, neu);
      setUmbenennen(null);
      aktualisiere([ordnerId]);
      const serverName = neuerPfad ? neuerPfad.split('/').pop() : neu;
      zeigeToast(`Umbenannt in „${serverName}“`);
    } catch (e) { zeigeToast(fehlerText(e, 'Umbenennen fehlgeschlagen'), true); }
    finally { laeuft(d.id, false); }
  };

  const papierkorb = async (d: DriveEintrag) => {
    if (laufende.has(d.id)) return;
    if (papierkorbId !== d.id) {
      setPapierkorbId(d.id);
      if (papierkorbTimer.current) clearTimeout(papierkorbTimer.current);
      papierkorbTimer.current = setTimeout(() => setPapierkorbId(null), 5000);
      return;
    }
    if (papierkorbTimer.current) clearTimeout(papierkorbTimer.current);
    setPapierkorbId(null);
    laeuft(d.id, true);
    const von = ordnerId;
    try {
      await verschiebeInPapierkorb(d.id);
      // #612: Gelöschtes nicht im Portal stehen lassen
      const dokId = a.metaNachDatei.get(d.id)?.id;
      if (dokId && (freigaben.get(dokId)?.length ?? 0) > 0 && !(await entziehe([dokId], null))) meldeFreigabenGeaendert();
      if (a.dateiId === d.id) a.waehleDatei(null);
      aktualisiere([von]);
      zeigeToast(`„${d.name}“ in den Papierkorb verschoben`, false, {
        label: 'Rückgängig',
        onClick: () => {
          stelleWiederHer(d.id)
            .then(() => { aktualisiere([von]); zeigeToast(`„${d.name}“ wiederhergestellt`); })
            .catch((e: unknown) => zeigeToast(fehlerText(e, 'Wiederherstellen fehlgeschlagen'), true));
        },
      });
    } catch (e) { zeigeToast(fehlerText(e, 'Verschieben in den Papierkorb fehlgeschlagen'), true); }
    finally { laeuft(d.id, false); }
  };

  const [verschiebtGerade, setVerschiebtGerade] = useState(false);
  const fuehreVerschiebenAus = async (ziel: BaumKnoten) => {
    const d = verschieben;
    const von = ordnerId;
    if (!d || !von || ziel.gdriveId === von) return;
    setVerschiebtGerade(true);
    try {
      await verschiebeEintrag(d.id, ziel.gdriveId, von);
      setVerschieben(null);
      if (a.dateiId === d.id) a.waehleDatei(null);
      aktualisiere([von, ziel.gdriveId]);
      zeigeToast(`Verschoben nach ${ziel.pfad.split('/').slice(-2).join(' › ')}`, false, {
        label: 'Rückgängig',
        onClick: () => {
          verschiebeEintrag(d.id, von, ziel.gdriveId)
            .then(() => { aktualisiere([von, ziel.gdriveId]); zeigeToast(`„${d.name}“ zurückverschoben`); })
            .catch((e: unknown) => zeigeToast(fehlerText(e, 'Zurückverschieben fehlgeschlagen'), true));
        },
      });
    } catch (e) { zeigeToast(fehlerText(e, 'Verschieben fehlgeschlagen'), true); }
    finally { setVerschiebtGerade(false); }
  };

  const perEmail = async (d: DriveEintrag) => {
    const { data: konto, error } = await supabase.from('email_konten').select('id, email_adresse, bezeichnung').eq('aktiv', true).limit(1).maybeSingle();
    if (error || !konto) { zeigeToast(error ? error.message : 'Kein E-Mail-Konto eingerichtet', true); return; }
    openForm(
      <EmailVerfassen
        variant="pille"
        konto={{ id: konto.id as string, email_adresse: konto.email_adresse as string, bezeichnung: (konto.bezeichnung as string | null) || (konto.email_adresse as string) }}
        autoAnhang={{ fileId: d.id, name: d.name, mimeType: d.mimeType }}
        draft={{ an: [], betreff: trenneEndung(d.name).basis.replace(/_/g, ' '), text_inhalt: '' }}
        onClose={() => closeForm()}
        onSent={() => closeForm()}
      />,
      'Datei per E-Mail senden',
      640, MAIL_FORM_META,
    );
  };

  // „Zur Signatur“: PDF aus Drive als neuen Signatur-Entwurf anlegen und im Editor öffnen
  const [signaturLaeuft, setSignaturLaeuft] = useState(false);
  const zurSignatur = async (d: DriveEintrag) => {
    setSignaturLaeuft(true);
    try {
      const blob = await ladeDateiBlob(d.id, d.mimeType);
      const id = await legeEntwurfAusPdfAn(blob, d.name, userId);
      meldeDokumenteGeaendert();
      navigate(`/admin/dokumente?tab=sign&bearbeiten=${encodeURIComponent(id)}`);
    } catch (e) { zeigeToast(fehlerText(e, 'Signatur-Vorgang konnte nicht angelegt werden'), true); }
    finally { setSignaturLaeuft(false); }
  };

  // ── Upload ───────────────────────────────────────────────────────────────
  const dateiInput = useRef<HTMLInputElement>(null);
  const uploadLaeuft = useRef(false);
  const [upload, setUpload] = useState<UploadStand | null>(null);
  const [ziehen, setZiehen] = useState(false);
  const ziehTiefe = useRef(0);

  const hochladen = useCallback(async (liste: File[]) => {
    if (!ordnerId || liste.length === 0) return;
    if (uploadLaeuft.current) { zeigeToast('Bitte warten – ein Upload läuft bereits.', true); return; }
    uploadLaeuft.current = true;
    const ziel = ordnerId;
    let ok = 0;
    const fehler: string[] = [];
    try {
      for (let i = 0; i < liste.length; i++) {
        setUpload({ name: liste[i].name, aktuell: i + 1, gesamt: liste.length });
        try {
          const { dokumentId } = await ladeDateiHoch(ziel, liste[i]);
          if (dokumentId) starteTextauszug(dokumentId);
          ok++;
        } catch (e) { fehler.push(`${liste[i].name}: ${fehlerText(e, 'Upload fehlgeschlagen')}`); }
      }
    } finally { uploadLaeuft.current = false; setUpload(null); }
    if (ok > 0) aktualisiere([ziel]);
    if (fehler.length === 0) zeigeToast(ok === 1 ? '1 Datei hochgeladen' : `${ok} Dateien hochgeladen`);
    else zeigeToast(`${ok} hochgeladen, ${fehler.length} fehlgeschlagen – ${fehler[0]}`, true);
  }, [ordnerId, aktualisiere, zeigeToast]);

  const oeffneDateiwahl = useCallback(() => {
    if (!a.wurzelId) { zeigeToast(a.objekt ? 'Dieses Objekt hat keinen Drive-Ordner.' : 'Bitte zuerst ein Objekt wählen.', true); return; }
    dateiInput.current?.click();
  }, [a.wurzelId, a.objekt, zeigeToast]);
  useEffect(() => { hochladenRef.current = oeffneDateiwahl; return () => { hochladenRef.current = null; }; }, [hochladenRef, oeffneDateiwahl]);

  useEffect(() => {
    const zurueck = () => { ziehTiefe.current = 0; setZiehen(false); };
    window.addEventListener('dragend', zurueck);
    window.addEventListener('drop', zurueck);
    return () => { window.removeEventListener('dragend', zurueck); window.removeEventListener('drop', zurueck); };
  }, []);
  const hatDateien = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  const ziehProps = ordnerId ? {
    onDragEnter: (e: React.DragEvent) => { if (!hatDateien(e)) return; e.preventDefault(); ziehTiefe.current++; setZiehen(true); },
    onDragOver: (e: React.DragEvent) => { if (hatDateien(e)) e.preventDefault(); },
    onDragLeave: (e: React.DragEvent) => { if (!hatDateien(e)) return; ziehTiefe.current = Math.max(0, ziehTiefe.current - 1); if (ziehTiefe.current === 0) setZiehen(false); },
    onDrop: (e: React.DragEvent) => { e.preventDefault(); ziehTiefe.current = 0; setZiehen(false); void hochladen(Array.from(e.dataTransfer.files)); },
  } : {};

  // ── Ordnerstruktur anlegen ───────────────────────────────────────────────
  const [legtAn, setLegtAn] = useState(false);
  const ordnerAnlegen = async () => {
    const o = a.objekt;
    if (!o) return;
    setLegtAn(true);
    try {
      await legeOrdnerstrukturAn(o.id);
      a.neuLadenObjekte();
      meldeDokumenteGeaendert();
      zeigeToast(`Ordnerstruktur für ${[o.nummer, o.name].filter(Boolean).join(' ')} angelegt`);
    } catch (e) { zeigeToast(fehlerText(e, 'Ordnerstruktur konnte nicht angelegt werden'), true); }
    finally { setLegtAn(false); }
  };

  // ── Ableitungen für die Anzeige ──────────────────────────────────────────
  const ordnerEintraege = a.inhalt.filter(e => e.mimeType === ORDNER_MIME);
  const dateiEintraege = a.inhalt.filter(e => e.mimeType !== ORDNER_MIME);
  const amWurzel = !!a.wurzelId && a.ordnerId === a.wurzelId;
  const zaehlerText = amWurzel
    ? `${a.dateienGesamt.toLocaleString('de-DE')} ${a.dateienGesamt === 1 ? 'Datei' : 'Dateien'} gesamt`
    : `${ordnerEintraege.length ? `${ordnerEintraege.length} Ordner · ` : ''}${dateiEintraege.length} ${dateiEintraege.length === 1 ? 'Datei' : 'Dateien'}`;
  const ordnerName = a.segmente.map(s => s.name).join(' › ');
  const unterordner = (e: DriveEintrag): UnterordnerInfo => {
    const k = a.index.nachId.get(e.id);
    if (k) return { dateien: a.zaehler.get(k.pfad) ?? 0, ordner: k.kinder.length, pfad: k.pfad };
    return { dateien: null, ordner: null, pfad: aktuellerKnoten ? `${aktuellerKnoten.pfad}/${e.name}` : e.name };
  };
  const gewaehlteDatei = a.dateiId ? dateiEintraege.find(d => d.id === a.dateiId) ?? null : null;
  const passtFilter = (e: DriveEintrag) => {
    if (freigabeFilter === 'alle' || e.mimeType === ORDNER_MIME) return true;
    const n = freigabenVon(e)?.length ?? 0;
    return freigabeFilter === 'frei' ? n > 0 : n === 0;
  };
  const inhaltGefiltert = a.inhalt.filter(passtFilter);
  const markierbar = inhaltGefiltert.filter(e => e.mimeType !== ORDNER_MIME && freigabenVon(e) !== null);
  const alleMarkiert = markierbar.length > 0 && markierbar.every(e => markiert.has(e.id));
  const markierteDateien = dateiEintraege.filter(e => markiert.has(e.id));
  const markierteDoks: FreigabeDok[] = markierteDateien.flatMap(e => {
    const m = a.metaNachDatei.get(e.id);
    return m ? [{ id: m.id, titel: m.titel || m.datei_name || e.name, objekt_id: m.objekt_id ?? a.objektId ?? null, mieter_id: m.mieter_id ?? null,
      eigentuemer_id: m.eigentuemer_id ?? null, kategorie: m.kategorie, dokument_typ: m.dokument_typ ?? null }] : [];
  });
  const markierteFrei = markierteDoks.filter(d => (freigaben.get(d.id)?.length ?? 0) > 0).length;
  const markierung: Markierung = {
    an: markiert,
    umschalten: e => setMarkiert(alt => { const n = new Set(alt); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; }),
    freigaben: freigabenVon,
  };
  const markierteHerunterladen = async () => { for (const e of markierteDateien) if (!istGoogleFormat(e.mimeType)) await herunterladen(e); };
  const markierteEntziehen = async () => {
    setEntziehtGerade(true);
    const f = await entziehe(markierteDoks.map(d => d.id), null);
    setEntziehtGerade(false); setEntziehenFrage(false);
    if (f) { zeigeToast(`Nicht entzogen: ${f}`, true); return; }
    zeigeToast(`Freigaben von ${markierteDoks.length === 1 ? '1 Dokument' : `${markierteDoks.length} Dokumenten`} entzogen`);
    meldeFreigabenGeaendert();
  };

  const aktionen: DateiAktionen = {
    ausgewaehltId: a.dateiId, umbenennenId: umbenennen?.id ?? null, umbenennenWert: umbenennen?.wert ?? '', papierkorbId, laufende,
    aktionenNurAusgewaehlt: schmal,
    onWahl: d => a.waehleDatei(a.dateiId === d.id ? null : d.id),
    onHerunterladen: d => void herunterladen(d),
    onUmbenennenStart: d => { setAnsicht('liste'); setUmbenennen({ id: d.id, wert: trenneEndung(d.name).basis }); },
    onUmbenennenWert: v => setUmbenennen(u => (u ? { ...u, wert: v } : u)),
    onUmbenennenOk: d => void bestaetigeUmbenennen(d),
    onUmbenennenAbbruch: () => setUmbenennen(null),
    onVerschieben: d => setVerschieben(d),
    onPapierkorb: d => void papierkorb(d),
  };

  // Layout: Desktop Baum 260 px sticky, Vorschau ab 1360 px als dritte Spalte; mobil Baum oben (< 700) bzw. 210 px daneben.
  const vorschauDaneben = !mobil && breite >= 1360;
  const spalten = schmal ? 'minmax(0,1fr)' : mobil ? '210px minmax(0,1fr)' : `260px minmax(0,1fr)${vorschauDaneben && gewaehlteDatei ? ' 340px' : ''}`;
  const baumStil: React.CSSProperties = schmal ? { maxHeight: 210 } : mobil ? { maxHeight: 'calc(100dvh - 140px)' } : { position: 'sticky', top: 16, maxHeight: 'calc(100dvh - 32px)' };
  const knopfGroesse = mobil ? 44 : 36;

  const leer = (
    <div className="flex flex-col items-center justify-center text-center" style={{ padding: '40px 16px', gap: 10 }}>
      <FolderOpen weight="duotone" size={40} color="var(--ink3)" />
      <span style={{ fontSize: 15, fontWeight: 800 }}>Dieser Ordner ist leer.</span>
      <span style={{ fontSize: 13, color: 'var(--ink3)' }}>
        Dateien hierher ziehen oder{' '}
        <button type="button" onClick={oeffneDateiwahl} className="cursor-pointer" style={{ border: 'none', background: 'none', padding: 0, color: 'var(--acc)', fontWeight: 700, fontSize: 13 }}>hochladen</button>.
      </span>
    </div>
  );

  const suchOrt = a.suchBereich === 'ordner' ? a.segmente[a.segmente.length - 1]?.name
    : a.suchBereich === 'objekt' ? (a.objekt ? [a.objekt.nummer, a.objekt.name].filter(Boolean).join(' ') : null) : null;
  const suchPlatzhalter = suchOrt ? `In „${suchOrt}“ suchen (Name und Inhalt)…` : 'Gesamte Ablage durchsuchen (Name und Inhalt)…';

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <input ref={dateiInput} type="file" multiple hidden onChange={e => { const f = Array.from(e.target.files ?? []); e.target.value = ''; void hochladen(f); }} />

      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <ObjektPicker optionen={pickerOptionen} wert={a.objektId} onWahl={id => a.waehleObjekt(id)} />
        <label className="immo-glas flex items-center" style={{ flex: '1 1 260px', minWidth: 0, height: 44, borderRadius: 22, gap: 8, padding: mobil ? '0 0 0 14px' : '0 4px 0 14px' }}>
          <MagnifyingGlass size={16} color="var(--ink3)" aria-hidden />
          <input value={suchText} onChange={e => { setSuchText(e.target.value); a.setzeSuche(e.target.value); }}
            placeholder={suchPlatzhalter} aria-label={suchPlatzhalter}
            className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
          {/* #558: Suchbereich — im Ordner sucht die Suche voreingestellt nur dort (samt Unterordnern) */}
          {a.objektId && (
            <span role="radiogroup" aria-label="Suchbereich" className="flex shrink-0" style={{ padding: 3, gap: 2, borderRadius: 15, background: 'var(--fill2)' }}>
              {([['ordner', 'Ordner', 'Nur im aktuellen Ordner samt Unterordnern suchen'], ['objekt', 'Objekt', 'Im ganzen Objekt suchen'], ['alle', 'Überall', 'In der gesamten Ablage suchen']] as const)
                .filter(([b]) => b !== 'ordner' || (a.ordnerId && a.ordnerId !== a.wurzelId))
                .map(([b, label, tip]) => {
                  const an = a.suchBereich === b;
                  return (
                    <button key={b} type="button" role="radio" aria-checked={an} data-tip={tip} onClick={e => { e.preventDefault(); a.setzeSuchBereich(b); }}
                      className="cursor-pointer" style={{ height: mobil ? 38 : 28, padding: '0 10px', borderRadius: 11, border: 'none', fontFamily: 'inherit', fontSize: 12, fontWeight: an ? 800 : 600,
                        background: an ? 'var(--bg)' : 'transparent', color: an ? 'var(--ink)' : 'var(--ink3)', boxShadow: an ? '0 2px 6px -3px var(--shade)' : 'none' }}>
                      {label}
                    </button>
                  );
                })}
            </span>
          )}
          {suchText && (
            <button type="button" aria-label="Suche leeren" onClick={() => { setSuchText(''); a.setzeSuche(''); }} className="flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: mobil ? 44 : 36, height: mobil ? 44 : 36, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
              <X weight="bold" size={11} />
            </button>
          )}
        </label>
        <div className="flex items-center" style={{ gap: 10, marginLeft: schmal ? 0 : undefined }}>
          <AnsichtUmschalter wert={ansicht} onChange={wechsleAnsicht} hoehe={mobil ? 44 : 34} />
          <ImmoButton art="akzent" groesse={44} icon={UploadSimple} onClick={oeffneDateiwahl} disabled={!!upload}>Hochladen</ImmoButton>
        </div>
      </div>

      {a.objekte?.find(o => o.id === a.objektId)?.istVerwaltung && !a.sucheAktiv && <VerwaltungHinweis />}

      {a.objekteFehler && <LeerZustand icon={WarningCircle} titel="Objekte konnten nicht geladen werden" text={a.objekteFehler} />}

      {a.sucheAktiv ? (
        <SuchErgebnisse treffer={a.treffer} laedt={a.sucheLaedt} fehler={a.sucheFehler}
          onOeffnen={t => (istInterneAblage(t.pfad) ? window.open(driveDateiUrl(t.id), '_blank', 'noopener') : a.oeffneTreffer(t))}
          onHerunterladen={async t => { const mime = t.mimeType ?? 'application/octet-stream'; if (!istGoogleFormat(mime)) await herunterladen({ id: t.id, name: t.name, mimeType: mime }); }}
          onMeldung={zeigeToast} touch={mobil} />
      ) : !a.objekte ? (
        <div aria-busy="true" className="animate-pulse" style={{ height: 320, borderRadius: 26, background: 'var(--fill2)' }} />
      ) : !a.objekt ? (
        <LeerZustand icon={Buildings} titel="Kein Objekt gewählt" text="Wähle oben ein Objekt, um seine Ablage zu öffnen." />
      ) : !a.wurzelId ? (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '40px 20px', gap: 12 }}>
          <span className="flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--warn-soft)' }}><FolderDashed weight="fill" size={30} color="var(--warn)" /></span>
          <span style={{ fontSize: 18, fontWeight: 800 }}>Dieses Objekt hat keinen Drive-Ordner.</span>
          <span style={{ fontSize: 13, color: 'var(--ink2)', maxWidth: 440 }}>Die Ordnerstruktur 00 Stammdaten bis 99 Archiv wird angelegt, sobald für das Objekt eine Verwaltungsart gesetzt ist.</span>
          {a.objekt.kannAnlegen ? (
            <ImmoButton art="akzent" groesse={44} icon={FolderPlus} onClick={() => void ordnerAnlegen()} disabled={legtAn}>{legtAn ? 'Wird angelegt …' : 'Ordnerstruktur anlegen'}</ImmoButton>
          ) : (
            <ImmoButton art="akzent" groesse={44} icon={Buildings} onClick={() => navigate(`/admin/objekte/${a.objekt?.id}`)}>Verwaltungsart im Objekt setzen</ImmoButton>
          )}
        </div>
      ) : (
        <div className="grid items-start" style={{ gridTemplateColumns: spalten, gap: 14 }}>
          <FolderTree
            wurzel={{ id: a.wurzelId, name: [a.objekt.nummer, a.objekt.verwaltungsart, a.objekt.name].filter(Boolean).join(' ') }}
            knoten={baumKnoten} offen={offen} onUmschalten={umschalten} aktivId={a.ordnerId}
            onWahl={id => a.oeffneOrdner(id)} style={baumStil} zeilenHoehe={mobil ? 44 : 36}
          />
          <section aria-label="Ordnerinhalt" className="immo-glas relative flex flex-col min-w-0" style={{ borderRadius: 26, padding: 14, gap: 12, minHeight: 280 }} {...ziehProps}>
            <InhaltKopf segmente={a.segmente} zaehlerText={zaehlerText} ordnerId={a.ordnerId ?? a.wurzelId} onSegment={s => a.oeffneOrdner(s.id)} hoehe={mobil ? 44 : 34} />
            {upload && (
              <div className="flex flex-col" style={{ gap: 6 }} role="status" aria-live="polite">
                <span className="truncate" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>Lade hoch: {upload.name} · {upload.aktuell} von {upload.gesamt}</span>
                <span style={{ height: 6, borderRadius: 3, background: 'var(--fill3)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.round(((upload.aktuell - 0.5) / upload.gesamt) * 100)}%`, background: 'var(--acc)', transition: 'width .3s' }} />
                </span>
              </div>
            )}
            {a.baumFehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Ordnerbaum: {a.baumFehler}</span>}
            {dateiEintraege.length > 0 && (
              <div className="flex items-center flex-wrap" style={{ gap: 8, padding: '6px 8px', borderRadius: 16, background: markiert.size ? 'var(--acc-soft)' : 'var(--fill)' }}>
                <AuswahlKaestchen an={alleMarkiert} label="Alle Dateien in diesem Ordner auswählen" gesperrt={markierbar.length === 0} touch={mobil}
                  onUmschalten={() => setMarkiert(alleMarkiert ? new Set() : new Set(markierbar.map(e => e.id)))} />
                <span style={{ fontSize: 13, fontWeight: 800, minWidth: 90 }}>{markiert.size ? `${markiert.size} ausgewählt` : 'Alle auswählen'}</span>
                {markiert.size > 0 ? (
                  <span className="flex flex-wrap items-center" style={{ gap: 6, marginLeft: 'auto' }}>
                    <ImmoButton art="akzent" icon={Globe} onClick={() => setFreigabeDialog('neu')} disabled={!markierteDoks.length}>Freigeben</ImmoButton>
                    <ImmoButton icon={PencilSimple} onClick={() => setFreigabeDialog('aendern')} disabled={!markierteDoks.length}>Freigabe ändern</ImmoButton>
                    <ImmoButton icon={LockSimple} onClick={() => setEntziehenFrage(true)} disabled={!markierteFrei}>Entziehen</ImmoButton>
                    <ImmoButton icon={DownloadSimple} onClick={() => { void markierteHerunterladen(); }}>Herunterladen</ImmoButton>
                    <PapierkorbKnopf eintraege={markierteDateien.map(e => ({ driveId: e.id, name: e.name, dokumentId: a.metaNachDatei.get(e.id)?.id ?? null }))}
                      zeige={zeigeToast} onFertig={(art, ids) => { setMarkiert(new Set()); if (art === 'geloescht' && a.dateiId && ids.includes(a.dateiId)) a.waehleDatei(null); aktualisiere([ordnerId]); }} />
                    <ImmoButton icon={X} aria-label="Auswahl aufheben" onClick={() => setMarkiert(new Set())} />
                  </span>
                ) : (
                  <Segment ariaLabel="Nach Freigabe filtern" hoehe={mobil ? 44 : 30} wert={freigabeFilter} onChange={setFreigabeFilter} style={{ marginLeft: 'auto' }}
                    eintraege={[{ key: 'alle', label: 'Alle' }, { key: 'frei', label: 'Freigegeben' }, { key: 'nicht', label: 'Nicht freigegeben' }]} />
                )}
              </div>
            )}
            <OrdnerInhalt
              markierung={markierung}
              eintraege={inhaltGefiltert} ansicht={ansicht} laedt={a.inhaltLaedt} dateienLadenNoch={a.dateienLadenNoch} fehler={a.inhaltFehler}
              onNeu={a.neuLadenInhalt} unterordner={unterordner} metaNachDatei={a.metaNachDatei} ordnerName={ordnerName}
              aktionen={aktionen} onOrdner={e => a.oeffneOrdner(e.id, e.name)} knopfGroesse={knopfGroesse}
              leer={freigabeFilter !== 'alle' && a.inhalt.length ? <span style={{ padding: 24, fontSize: 13, color: 'var(--ink3)', textAlign: 'center' }}>{freigabeFilter === 'frei' ? 'Keine freigegebenen Dateien in diesem Ordner.' : 'Alle Dateien in diesem Ordner sind freigegeben.'}</span> : leer}
            />
            {ziehen && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center" aria-hidden
                style={{ borderRadius: 26, gap: 6, padding: 16, background: 'color-mix(in srgb, var(--bg) 85%, transparent)', boxShadow: 'inset 0 0 0 2px var(--acc)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', pointerEvents: 'none', zIndex: 5 }}>
                <CloudArrowUp weight="fill" size={44} color="var(--acc)" />
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--acc)' }}>Loslassen zum Hochladen</span>
                <span style={{ fontSize: 13, color: 'var(--ink2)' }}>nach {ordnerName}</span>
              </div>
            )}
          </section>
          {gewaehlteDatei && (
            <div style={{ gridColumn: vorschauDaneben ? undefined : '1 / -1', ...(vorschauDaneben ? { position: 'sticky', top: 16 } : {}) }}>
              <VorschauPanel
                datei={gewaehlteDatei} meta={a.metaNachDatei.get(gewaehlteDatei.id) ?? null} ordnerName={ordnerName}
                onSchliessen={() => a.waehleDatei(null)} onHerunterladen={() => void herunterladen(gewaehlteDatei)}
                onUmbenennen={() => aktionen.onUmbenennenStart(gewaehlteDatei)} onVerschieben={() => setVerschieben(gewaehlteDatei)}
                onEmail={() => void perEmail(gewaehlteDatei)}
                onSignatur={() => void zurSignatur(gewaehlteDatei)} signaturLaeuft={signaturLaeuft}
                onAnKollegen={() => setAnKollegen(gewaehlteDatei)}
                onPost={() => setPerPost(gewaehlteDatei)}
                onSchreiben={() => navigate(`/admin/schreiben/neu?anlage=${encodeURIComponent(`drive:${gewaehlteDatei.id}:${gewaehlteDatei.name}`)}`)}
              />
              {anKollegen && (
                <AnKollegenSendenDialog
                  anhang={{ quelle: 'drive', id: anKollegen.id, name: anKollegen.name, typ: anKollegen.mimeType,
                    kontext: a.objekt ? [a.objekt.nummer, a.objekt.name].filter(Boolean).join(' ') : null }}
                  onSchliessen={() => setAnKollegen(null)} onFertig={(m) => { setAnKollegen(null); zeigeToast(m); }} />
              )}
              {perPost && (
                <PostVersandDialog
                  anlagen={[{ name: perPost.name, mime: perPost.mimeType, laden: () => ladeDateiBlob(perPost.id, perPost.mimeType), quelle: { drive: perPost.id } }]}
                  start={{ objektId: a.objekt && !a.objekt.pseudo ? a.objekt.id : null }}
                  bezug={{ typ: 'dokument', id: a.metaNachDatei.get(perPost.id)?.id ?? null }}
                  onSchliessen={() => setPerPost(null)} onGesendet={(m) => { setPerPost(null); zeigeToast(m); }} />
              )}
            </div>
          )}
        </div>
      )}

      {verschieben && (
        <VerschiebenDialog dateiName={verschieben.name} baum={a.baum} aktuellerOrdnerId={ordnerId} laeuft={verschiebtGerade}
          onVerschieben={z => void fuehreVerschiebenAus(z)} onAbbrechen={() => setVerschieben(null)} />
      )}

      {freigabeDialog && markierteDoks.length > 0 && (
        <FreigabeDialog dokumente={markierteDoks} aendern={freigabeDialog === 'aendern'} onSchliessen={() => setFreigabeDialog(null)}
          onFertig={m => { setFreigabeDialog(null); zeigeToast(m); setMarkiert(new Set()); meldeFreigabenGeaendert(); }} />
      )}
      {entziehenFrage && (
        <BestaetigenDialog titel="Freigaben entziehen?" bestaetigenLabel="Entziehen" icon={LockSimple} laeuft={entziehtGerade}
          text={`${markierteFrei === 1 ? '1 Dokument ist' : `${markierteFrei} Dokumente sind`} danach in keinem Portal mehr sichtbar. Freigeben ist jederzeit wieder möglich.`}
          onBestaetigen={() => { void markierteEntziehen(); }} onAbbrechen={() => setEntziehenFrage(false)} />
      )}

      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
}
